export const wsError = {
    ERR_NO_INTERNET_CONNECTION: -5,
    ERR_TIMEOUT: -6,
};

const REQUEST_TIMEOUT_MILLIS = 7000;
const PING_INTERVAL = 7000;

function _createRequest(method, request_id, data) {
    return {
        m: method,
        r: request_id,
        d: data,
    };
}

class RequestObj {
    constructor(wsWrapper, request_id, request, CB) {
        this.wsWrapper = wsWrapper;
        this.request_id = request_id;
        this.request = request;
        this.CB = CB;
        this.timeout = null;
    }

    cancel() {
        this.clearTimeout();
        if (this.wsWrapper) this.wsWrapper.cancelReq(this.request_id);
    }

    clearTimeout() {
        if (!this.timeout) return;
        clearTimeout(this.timeout);
        this.timeout = null;
    }
}

export default class WsWrapper {
    constructor(ws, lifecycleListener) {
        this.ws = ws;
        this.requestId = 0;
        this.requests = {};
        this.staticListeners = {};
        this.lifecycleListeners = new Set();
        this._interceptor = null;
        this._pingInterval = null;
        if (lifecycleListener) {
            this.subscribeLifecycleListener(lifecycleListener);
        }
        this._init();
    }

    destroy() {
        this.ws.destroy();
        clearInterval(this._pingInterval);
    }

    set interceptor(mInterceptor) {
        this._interceptor = mInterceptor;
    }

    _init() {
        this.ws.onMessageListener = this._handleMessage.bind(this);
        this.ws.lifecycleListener = this;
    }

    sendMessage(wsRequest, CB, timeout) {
        if (!this.ws || !wsRequest) {
            return;
        }

        let rID = ++this.requestId;

        let req = _createRequest(wsRequest.method, rID, wsRequest.data);

        return this._sendMsg(rID, req, CB, timeout);
    }

    _sendMsg(rID, req, CB, timeout = REQUEST_TIMEOUT_MILLIS) {
        let reqObj = new RequestObj(this, rID, req, CB);

        if (CB) {
            this.requests[rID] = reqObj;

            let ie = this._internalError.bind(this, rID, wsError.ERR_TIMEOUT);

            reqObj.timeout = setTimeout(function () {
                ie();
            }, timeout);
        }

        let msg = JSON.stringify(req);

        //TODO debug log
       // console.log('SEND MESSAGE', msg);

        let result = this.ws.sendMessage(msg);

        if (!result) {
            this._internalError(rID, wsError.ERR_NO_INTERNET_CONNECTION);
        }

        return reqObj;
    }

    cancelReq(requestId) {
        delete this.requests[requestId];
    }

    isConnected() {
        return this.ws.isConnected;
    }

    subscribeLifecycleListener(listener) {
        this.lifecycleListeners.add(listener);
    }

    onopen(event) {
        this.lifecycleListeners.forEach((l) => {
            if (l.hasOwnProperty('onopen')) {
                try {
                    l.onopen(event);
                } catch (e) {
                    console.log(e);
                }
            }
        });

        this._startPing();
    }


    onerror(event) {
        this.lifecycleListeners.forEach((l) => {
            if (l.hasOwnProperty('onerror')) {
                try {
                    l.onerror(event);
                } catch (e) {
                    console.log(e);
                }
            }
        });
    }

    onclose(event) {
        this.lifecycleListeners.forEach((l) => {
            if (l.hasOwnProperty('onclose')) {
                try {
                    l.onclose(event);
                } catch (e) {
                    console.log(e);
                }
            }
        });
    }

    unsubscribeLifecycleListener(listener) {
        try {
            this.lifecycleListeners.delete(listener);
        } catch (e) {
            console.log(e);
        }
    }

    // adds listener for emit with such method
    subscribeStatic(method, CB) {
        if (!CB) return;
        this.staticListeners[method] = CB;
    }

    unsubscribeStatic(method) {
        if (!method) return;
        delete this.staticListeners[method];
    }

    _internalError(requestId, code) {
        if (this.requests.hasOwnProperty(requestId) && this.requests[requestId]) {
            let obj = this.requests[requestId];
            obj.cancel();
            obj.CB({re: false, d: null, err: {c: code, msg: ''}});
        }
    }

    _handleMessage(message) {

        if (message instanceof ArrayBuffer) {
            if (message.byteLength === 8) {
                this._handlePing(message);
            }

            return
        }

        let data;

        try {
            //TODO debug log
            //console.log('HANDLE MESSAGE', message);
            data = JSON.parse(message);
        } catch (e) {
            console.log(e);
            return;
        }

        let rId = data.r;

        if (this._interceptor && this._interceptor.hasOwnProperty('intercept')) {
            if (this._interceptor.intercept(data)) {
                let obj = this.requests[rId];
                if (obj) obj.cancel();

                return;
            }
        }

        if (!rId) {
            if (!data.m) return;

            let CB = this.staticListeners[data.m];

            if (!CB) return;

            CB({re: data.re, d: data.d, err: data.err});
        }

        let obj = this.requests[rId];

        if (!obj) return;

        obj.cancel();

        if (obj.CB) {
            obj.CB({re: data.re, d: data.d, err: data.err});
        }
    }

    setReconnect(reconnect) {
        this.ws.setReconnect(reconnect);
    }

    shouldReconnect() {
        return this.ws.shouldReconnect();
    }

    _ping() {
         try {
            let result = this.ws.sendMessage(bigIntToByteArrayUInt64(BigInt(Date.now()) * 1000000n));

            if (!result) {
                //console.log(`ping ${Infinity}`);
            }
        } catch (e) {
            let result = this.ws.sendMessage(new ArrayBuffer(8));

            if (!result) {
                //console.log(`ping ${Infinity}`);
            }
        }
    }

    _handlePing(msg) {
        let latency;
        try {
            latency = Date.now() - Number(byteArrayUInt64ToBigInt(msg)/1000000n);
        }
        catch (e) {
            latency = "unknown"
        }
        //console.log(`ping ${latency}`);

        this.lifecycleListeners.forEach((l) => {
            if (l.hasOwnProperty('connectionInfo')) {
                try {
                    l.connectionInfo({
                        latency: latency,
                    });
                } catch (e) {
                    console.log(e);
                }
            }
        });
    }

    sendPingMessage() {
        this._ping()
    }

    _startPing() {
        if (this._pingInterval) {
            return;
        }

        this._ping();
        this._pingInterval = setInterval(this._ping.bind(this), PING_INTERVAL);
    }
}

function bigIntToByteArrayUInt64(bigIntValue) {
    const arr = new ArrayBuffer(8);
    new DataView(arr).setBigUint64(0, BigInt.asUintN(64, bigIntValue), true);
    return arr;
}

function byteArrayUInt64ToBigInt(arr) {
    return BigInt.asIntN(64, new DataView(arr).getBigUint64(0, true));
}
